import React, { useRef, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { queryClient } from "App";
import { selectActiveSession } from "app/modules/schools/selectors";
import {
  getAllExpenseHead,
  getAllExpenseSubHead,
  requestExpense,
} from "app/services/ledgerAccount";
import { getTransport } from "app/services/student-management";
import { MenuProps } from "app/utils/helper";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";

// ============ Styled Components ============
const Label = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: "1.2rem",
  marginBottom: "10px",
  fontWeight: 500,
  color: "#0C2F49",
}));

const RequiredStar = () => <span style={{ color: "#DC2626" }}> *</span>;

// ============ Validation Schema ============
const requestExpenseSchema = z
  .object({
    heads: z.array(z.any()).default([]),
    subHeads: z.array(z.any()).default([]),
    transportList: z.array(z.string()).default([]),
    head: z.string().min(1, "Head Selection is required"),
    subHead: z.string().min(1, "Sub Head Selection is required"),
    vehicle: z.string().optional(),
    amt: z.preprocess(
      (val) =>
        val === "" || val === null || typeof val === "undefined"
          ? undefined
          : Number(val),
      z
        .number({ required_error: "Amount is required" })
        .positive("Amount is required"),
    ),
    purpose: z.string().trim().min(1, "Purpose is required"),
    isVehicle: z.boolean().default(false),
    description: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.isVehicle && !values.vehicle) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["vehicle"],
        message: "Vehicle Selection is required",
      });
    }
  });

// ============ Main Component ============
const RequestExpenseForm = ({
  dialogs,
  onSuccess,
  onOpenSubHeadDialog,
  onSubmittingChange = () => {},
}) => {
  const selectedSession = useSelector(selectActiveSession);
  const submitLockRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ============ Form ============
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(requestExpenseSchema),
    defaultValues: {
      subHeads: [],
      heads: [],
      head: "",
      subHead: "",
      vehicle: "",
      amt: "",
      purpose: "",
      isVehicle: false,
      transportList: [],
      description: "",
    },
  });

  const values = watch();

  const onSubmit = (formValues) => {
    if (isSubmitting || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    onSubmittingChange(true);

    const payload = {
      session: selectedSession,
      expenseSubHeadDocId: formValues.subHead,
      vehicleNumber: formValues.vehicle,
      requestedAmount: formValues.amt,
      purpose: formValues.purpose,
    };
    requestExpenseMutate(payload);
  };

  // ============ API Mutations ============
  const { mutate: requestExpenseMutate } = useMutation(requestExpense, {
    onSuccess: (res) => {
      toast.success(res?.data?.message);
      queryClient.invalidateQueries({ queryKey: "getAllExpense" });
      submitLockRef.current = false;
      setIsSubmitting(false);
      onSubmittingChange(false);
      onSuccess();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
      submitLockRef.current = false;
      setIsSubmitting(false);
      onSubmittingChange(false);
    },
  });

  // ============ API Queries ============
  useQuery({
    queryKey: ["getTransport"],
    queryFn: () => getTransport({ status: "ACTIVE" }),
    onSuccess: (res) => {
      const transportList =
        res?.data?.list?.map((item) => item.vehicleNumber) || [];
      setValue("transportList", transportList, { shouldValidate: false });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: values.isVehicle,
  });

  useQuery({
    queryKey: ["getAllExpenseHead", selectedSession],
    queryFn: getAllExpenseHead,
    onSuccess: (res) => {
      const heads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseHeadName,
        })) || [];

      setValue("heads", heads, { shouldValidate: false });
      if (dialogs?.headId) {
        setValue("head", dialogs.headId, { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: dialogs?.add ? true : false,
  });

  useQuery({
    queryKey: ["getAllExpenseSubHeadForReq", values.head],
    queryFn: () => getAllExpenseSubHead({ expenseHeadDocId: values.head }),
    onSuccess: (res) => {
      const subHeads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseSubHeadName,
          description: item?.description,
        })) || [];
      setValue("subHeads", subHeads, { shouldValidate: false });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: values.head ? true : false,
  });

  // ============ Render ============
  return (
    <form id="reqExpForm" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1} mt={1}>
        {/* Expense Head */}
        <Grid item xs={6} md={4}>
          <Label>
            Expense Head
            <RequiredStar />
          </Label>
          <CustomSelectBox
            name="head"
            value={values.head}
            displayEmpty
            fullWidth
            MenuProps={MenuProps}
            onChange={(e) => {
              setValue("head", e.target.value, { shouldValidate: true });
              setValue("subHead", "", { shouldValidate: true });
              setValue("description", "", { shouldValidate: false });
            }}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            {values?.heads?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </CustomSelectBox>
          {errors.head && (
            <Typography sx={{ color: "red" }}>{errors.head.message}</Typography>
          )}
        </Grid>

        {/* Sub Head */}
        <Grid item xs={6} md={4}>
          <Label>
            Sub Head
            <RequiredStar />
          </Label>
          <CustomSelectBox
            name="subHead"
            fullWidth
            disabled={!values.head}
            displayEmpty
            value={values.subHead}
            MenuProps={MenuProps}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            {values.subHeads?.map((item) => (
              <MenuItem
                key={item.id}
                value={item?.id}
                onClick={() => {
                  setValue("subHead", item.id, { shouldValidate: true });
                  setValue("description", item?.description || "", {
                    shouldValidate: false,
                  });
                }}
              >
                {item.name}
              </MenuItem>
            ))}
            <MenuItem
              sx={{
                color: "blue",
                textDecoration: "underline",
                fontStyle: "italic",
              }}
              value="Add New"
              onClick={() => {
                onOpenSubHeadDialog(values.head);
                setValue("subHead", "", { shouldValidate: true });
              }}
            >
              Add New
            </MenuItem>
          </CustomSelectBox>
          {errors.subHead && (
            <Typography sx={{ color: "red" }}>
              {errors.subHead.message}
            </Typography>
          )}
        </Grid>

        {/* Vehicle Expense */}
        <Grid item xs={6} md={4}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Label>Vehicle Expense ?</Label>
            <FormControlLabel
              sx={{ mt: "-10px" }}
              control={
                <Checkbox
                  checked={values.isVehicle}
                  onChange={(e) =>
                    setValue("isVehicle", e.target.checked, {
                      shouldValidate: true,
                    })
                  }
                />
              }
              label="Yes"
            />
          </Stack>
          {values.isVehicle && (
            <>
              <Label>
                Vehicle Number
                <RequiredStar />
              </Label>
              <CustomSelectBox
                displayEmpty
                name="vehicle"
                fullWidth
                value={values.vehicle}
                MenuProps={MenuProps}
                onChange={(e) => {
                  setValue("vehicle", e.target.value, { shouldValidate: true });
                }}
              >
                <MenuItem value="" disabled>
                  Select Vehicle
                </MenuItem>
                {values.transportList?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {errors.vehicle && (
                <Typography sx={{ color: "red" }}>
                  {errors.vehicle.message}
                </Typography>
              )}
            </>
          )}
        </Grid>

        {/* Sub Head Description */}
        <Grid item xs={12}>
          <Label>Sub Head Description</Label>
          <Typography>{values.description || "--"}</Typography>
        </Grid>

        {/* Amount */}
        <Grid item xs={6} md={4}>
          <Label>
            Amount
            <RequiredStar />
          </Label>
          <CustomTextField
            type="number"
            name="amt"
            fullWidth
            placeholder="Enter Amount"
            value={values.amt}
            onChange={(e) => {
              setValue("amt", e.target.value, { shouldValidate: true });
            }}
          />
          {errors.amt && (
            <Typography sx={{ color: "red" }}>{errors.amt.message}</Typography>
          )}
        </Grid>

        {/* Purpose */}
        <Grid item xs={6} md={8}>
          <Label>
            Purpose
            <RequiredStar />
          </Label>
          <CustomTextField
            name="purpose"
            fullWidth
            placeholder="Enter Purpose"
            value={values.purpose}
            onChange={(e) => {
              setValue("purpose", e.target.value, { shouldValidate: true });
            }}
          />
          {errors.purpose && (
            <Typography sx={{ color: "red" }}>
              {errors.purpose.message}
            </Typography>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default RequestExpenseForm;
